/** Sözleşmeye (contracts/out/openapi.yaml) göre yazılmış tipli uç sarmalayıcıları. */
import type {
  AckRequest, ActiveSession, RevokeOthersResult, Attachment, AuditLogAction, AuditLogPage, AuthSession, Ban, BetaCodeCheck, Channel,
  ChannelPosition, CompleteUploadRequest, CreateBanRequest, CreateChannelRequest, CreateGuildRequest,
  CreateInviteRequest, CreateMessageRequest, CreateRoleRequest, CreateUploadRequest, CurrentUser,
  GatewayGuild, GatewayInfo, Guild, GuildMember, Invite, InviteAcceptResult, InviteWithMetadata,
  LoginRequest, Message, PutOverwriteRequest, ReadState, RegisterRequest, Role, RolePosition,
  SetupRequest, SetupStatus, TransferOwnershipRequest, UpdateChannelRequest, UpdateCurrentUserRequest, UpdateGuildRequest,
  UpdateMemberRequest, UpdateMessageRequest, UpdateRoleRequest, UpdateUserSettingsRequest, UploadTicket,
  UserSettings, UsernameAvailability, User, ReadyStatus, UpdateVoiceStateRequest, VoiceJoinRequest, VoiceJoinResponse, VoiceState,
  InstanceSettings, InstanceSettingsPatch, SmtpTestRequest, InstanceInfo,
  AdminSystemLiveResponse, AdminSystemSeriesResponse, AdminPeriodReport, AdminCapacityModel,
  AdminCapacityPlan, AdminMediaSessionDetail, ErrorGroup, ErrorGroupListResponse, ErrorGroupDetailResponse,
  ErrorGroupStatus, AdminBetaCodeListResponse, AdminBetaCodeCreateRequest, AdminBetaCodeDeleteResponse,
  Relationship, DMChannel,
  Application, CreateApplicationRequest, UpdateApplicationRequest,
} from "@halkora/api-types";
import { isEmailNotVerified, request, requestWithHeaders } from "./client.ts";

export { isEmailNotVerified };

const enc = encodeURIComponent;
const j = <T>(path: string, method: string, body?: unknown, query?: Record<string, string | number | undefined>) =>
  request<T>(path, { method, body, query });

export const api = {
  // ── ilk kurulum (K45)
  setupStatus: () => request<SetupStatus>("/v1/setup", { method: "GET", auth: false }),
  setup: (b: SetupRequest) => request<AuthSession>("/v1/setup", { method: "POST", body: b, auth: false }),
  instanceInfo: () => request<InstanceInfo>("/v1/instance", { method: "GET", auth: false }),
  // ── kimlik
  checkBetaCode: (code: string) => request<BetaCodeCheck>("/v1/auth/beta-codes/check", { method: "POST", body: { code }, auth: false }),
  usernameAvailable: (username: string) => request<UsernameAvailability>("/v1/auth/username-available", { query: { username }, auth: false }),
  register: (b: RegisterRequest) => request<AuthSession>("/v1/auth/register", { method: "POST", body: b, auth: false }),
  login: (b: LoginRequest) => request<AuthSession>("/v1/auth/login", { method: "POST", body: b, auth: false }),
  logout: () => request<void>("/v1/auth/logout", { method: "POST", auth: false }),
  ready: () => request<ReadyStatus>("/ready", { auth: false }),
  // ── hesap (K1): e-posta doğrulama, parola sıfırlama, aktif oturumlar
  verifyEmail: (token: string) => request<void>("/v1/auth/email/verify", { method: "POST", body: { token }, auth: false }),
  resendVerification: () => j<void>("/v1/auth/email/resend", "POST"),
  forgotPassword: (email: string) => request<void>("/v1/auth/password/forgot", { method: "POST", body: { email }, auth: false }),
  resetPassword: (token: string, password: string) => request<void>("/v1/auth/password/reset", { method: "POST", body: { token, password }, auth: false }),
  sessions: () => j<ActiveSession[]>("/v1/auth/sessions", "GET"),
  revokeSession: (id: string) => j<void>(`/v1/auth/sessions/${id}`, "DELETE"),
  revokeOtherSessions: () => j<RevokeOthersResult>("/v1/auth/sessions/revoke-others", "POST"),
  gatewayInfo: () => request<GatewayInfo>("/v1/gateway"),
  // ── kullanıcı
  me: () => j<CurrentUser>("/v1/users/@me", "GET"),
  updateMe: (b: UpdateCurrentUserRequest) => j<CurrentUser>("/v1/users/@me", "PATCH", b),
  myGuilds: () => j<Guild[]>("/v1/users/@me/guilds", "GET"),
  leaveGuild: (id: string) => j<void>(`/v1/users/@me/guilds/${id}`, "DELETE"),
  readStates: () => j<ReadState[]>("/v1/users/@me/read-states", "GET"),
  settings: () => j<UserSettings>("/v1/users/@me/settings", "GET"),
  updateSettings: (b: UpdateUserSettingsRequest) => j<UserSettings>("/v1/users/@me/settings", "PATCH", b),
  user: (id: string) => j<User>(`/v1/users/${id}`, "GET"),
  // ── ses (Z5b doldurur; Z5a yalnız sinyalleşme)
  voiceJoin: (b: VoiceJoinRequest) => j<VoiceJoinResponse>("/v1/voice/join", "POST", b),
  voiceLeave: () => j<void>("/v1/voice/leave", "POST"),
  voiceState: (b: UpdateVoiceStateRequest) => j<VoiceState>("/v1/voice/state", "PATCH", b),
  // ── sunucu
  createGuild: (b: CreateGuildRequest) => j<GatewayGuild>("/v1/guilds", "POST", b),
  guild: (id: string, withCounts = false) => request<Guild>(`/v1/guilds/${id}`, { query: { with_counts: withCounts || undefined } }),
  updateGuild: (id: string, b: UpdateGuildRequest) => j<Guild>(`/v1/guilds/${id}`, "PATCH", b),
  deleteGuild: (id: string) => j<void>(`/v1/guilds/${id}`, "DELETE"),
  ackGuild: (id: string) => j<void>(`/v1/guilds/${id}/ack`, "POST"),
  transferOwnership: (id: string, b: TransferOwnershipRequest) => j<Guild>(`/v1/guilds/${id}/transfer-ownership`, "POST", b),
  // ── kanal
  createChannel: (guildId: string, b: CreateChannelRequest) => j<Channel>(`/v1/guilds/${guildId}/channels`, "POST", b),
  reorderChannels: (guildId: string, b: ChannelPosition[]) => j<void>(`/v1/guilds/${guildId}/channels`, "PATCH", b),
  updateChannel: (id: string, b: UpdateChannelRequest) => j<Channel>(`/v1/channels/${id}`, "PATCH", b),
  deleteChannel: (id: string) => j<void>(`/v1/channels/${id}`, "DELETE"),
  putOverwrite: (channelId: string, overwriteId: string, b: PutOverwriteRequest) => j<void>(`/v1/channels/${channelId}/permissions/${overwriteId}`, "PUT", b),
  deleteOverwrite: (channelId: string, overwriteId: string) => j<void>(`/v1/channels/${channelId}/permissions/${overwriteId}`, "DELETE"),
  // ── mesaj
  messages: (channelId: string, q: { before?: string; after?: string; around?: string; limit?: number } = {}) =>
    request<Message[]>(`/v1/channels/${channelId}/messages`, { query: q }),
  sendMessage: (channelId: string, b: CreateMessageRequest) => j<Message>(`/v1/channels/${channelId}/messages`, "POST", b),
  editMessage: (channelId: string, id: string, b: UpdateMessageRequest) => j<Message>(`/v1/channels/${channelId}/messages/${id}`, "PATCH", b),
  deleteMessage: (channelId: string, id: string) => j<void>(`/v1/channels/${channelId}/messages/${id}`, "DELETE"),
  ack: (channelId: string, id: string, b?: AckRequest) => j<void>(`/v1/channels/${channelId}/messages/${id}/ack`, "POST", b ?? {}),
  addReaction: (channelId: string, id: string, emoji: string) => j<void>(`/v1/channels/${channelId}/messages/${id}/reactions/${enc(emoji)}/@me`, "PUT"),
  removeReaction: (channelId: string, id: string, emoji: string) => j<void>(`/v1/channels/${channelId}/messages/${id}/reactions/${enc(emoji)}/@me`, "DELETE"),
  typing: (channelId: string) => j<void>(`/v1/channels/${channelId}/typing`, "POST"),
  // ── ek
  createUpload: (b: CreateUploadRequest) => j<UploadTicket>("/v1/attachments", "POST", b),
  completeUpload: (id: string, b?: CompleteUploadRequest) => j<Attachment>(`/v1/attachments/${id}/complete`, "POST", b ?? {}),
  attachmentUrl: (id: string) => j<{ url: string; expires_at: string }>(`/v1/attachments/${id}/url`, "GET"),
  // ── rol
  roles: (guildId: string) => j<Role[]>(`/v1/guilds/${guildId}/roles`, "GET"),
  createRole: (guildId: string, b: CreateRoleRequest) => j<Role>(`/v1/guilds/${guildId}/roles`, "POST", b),
  reorderRoles: (guildId: string, b: RolePosition[]) => j<Role[]>(`/v1/guilds/${guildId}/roles`, "PATCH", b),
  roleMemberCounts: (guildId: string) => j<Record<string, number>>(`/v1/guilds/${guildId}/roles/member-counts`, "GET"),
  updateRole: (guildId: string, id: string, b: UpdateRoleRequest) => j<Role>(`/v1/guilds/${guildId}/roles/${id}`, "PATCH", b),
  deleteRole: (guildId: string, id: string) => j<void>(`/v1/guilds/${guildId}/roles/${id}`, "DELETE"),
  // ── üye
  members: (guildId: string, q: { after?: string; limit?: number; query?: string; role_id?: string } = {}) =>
    request<GuildMember[]>(`/v1/guilds/${guildId}/members`, { query: q }),
  updateMember: (guildId: string, userId: string, b: UpdateMemberRequest) => j<GuildMember>(`/v1/guilds/${guildId}/members/${userId}`, "PATCH", b),
  kick: (guildId: string, userId: string, reason?: string) =>
    request<void>(`/v1/guilds/${guildId}/members/${userId}`, { method: "DELETE", headers: reason ? { "X-Audit-Log-Reason": enc(reason) } : undefined }),
  addMemberRole: (guildId: string, userId: string, roleId: string) => j<void>(`/v1/guilds/${guildId}/members/${userId}/roles/${roleId}`, "PUT"),
  removeMemberRole: (guildId: string, userId: string, roleId: string) => j<void>(`/v1/guilds/${guildId}/members/${userId}/roles/${roleId}`, "DELETE"),
  // ── yasak / denetim
  bans: (guildId: string, q: { before?: string; after?: string; limit?: number } = {}) => request<Ban[]>(`/v1/guilds/${guildId}/bans`, { query: q }),
  ban: (guildId: string, userId: string, b: CreateBanRequest, reason?: string) =>
    request<void>(`/v1/guilds/${guildId}/bans/${userId}`, { method: "PUT", body: b, headers: reason ? { "X-Audit-Log-Reason": enc(reason) } : undefined }),
  unban: (guildId: string, userId: string) => j<void>(`/v1/guilds/${guildId}/bans/${userId}`, "DELETE"),
  auditLogs: (guildId: string, q: { user_id?: string; action_type?: AuditLogAction; before?: string; limit?: number } = {}) =>
    request<AuditLogPage>(`/v1/guilds/${guildId}/audit-logs`, { query: q }),
  // ── davet
  createInvite: (channelId: string, b: CreateInviteRequest) => j<InviteWithMetadata>(`/v1/channels/${channelId}/invites`, "POST", b),
  guildInvites: (guildId: string) => j<InviteWithMetadata[]>(`/v1/guilds/${guildId}/invites`, "GET"),
  invite: (code: string) => j<Invite>(`/v1/invites/${code}`, "GET"),
  acceptInvite: (code: string) => j<InviteAcceptResult>(`/v1/invites/${code}`, "POST"),
  deleteInvite: (code: string) => j<void>(`/v1/invites/${code}`, "DELETE"),
  // ── sunucu ayarları (K45)
  instanceSettings: async () => {
    const { data, headers } = await requestWithHeaders<InstanceSettings>("/v1/settings");
    const etag = headers.get("ETag") ?? headers.get("etag") ?? undefined;
    return { settings: data, etag };
  },
  updateInstanceSettings: (b: InstanceSettingsPatch, etag?: string) =>
    request<InstanceSettings>("/v1/settings", {
      method: "PATCH",
      body: b,
      headers: etag ? { "If-Match": etag } : undefined,
    }),
  testSmtp: (b: SmtpTestRequest) =>
    request<void>("/v1/settings/smtp/test", { method: "POST", body: b }),
  // ── yönetici paneli (G42)
  adminSystemLive: () => j<AdminSystemLiveResponse>("/v1/admin/system/live", "GET"),
  adminSystemSeries: (range: "24h" | "7d" | "30d" | "365d") =>
    request<AdminSystemSeriesResponse>("/v1/admin/system/series", { query: { range } }),
  adminReports: (period: "day" | "week" | "month", at?: string) =>
    request<AdminPeriodReport>("/v1/admin/reports", { query: { period, at } }),
  adminCapacity: () => j<AdminCapacityModel>("/v1/admin/capacity", "GET"),
  adminCapacityPlan: (params: { users: number; voice_ratio?: number; stream_count?: number; viewers_per_stream?: number }) =>
    request<AdminCapacityPlan>("/v1/admin/capacity/plan", { query: params }),
  adminQualitySession: (sessionId: string) =>
    j<AdminMediaSessionDetail>(`/v1/admin/quality/sessions/${sessionId}`, "GET"),
  // ── hata grupları (G41)
  // beta davet kodları (yalnız instance admin)
  adminBetaCodes: () => j<AdminBetaCodeListResponse>("/v1/admin/beta-codes", "GET"),
  adminCreateBetaCodes: (body: AdminBetaCodeCreateRequest) =>
    j<AdminBetaCodeListResponse>("/v1/admin/beta-codes", "POST", body),
  adminDeleteBetaCode: (id: string) => j<AdminBetaCodeDeleteResponse>(`/v1/admin/beta-codes/${enc(id)}`, "DELETE"),
  adminErrorGroups: (query?: { status?: ErrorGroupStatus; source?: string; since?: string; limit?: number }) =>
    request<ErrorGroupListResponse>("/v1/admin/error-groups", { query }),
  adminErrorGroup: (fingerprint: string) =>
    j<ErrorGroupDetailResponse>(`/v1/admin/error-groups/${fingerprint}`, "GET"),
  adminUpdateErrorGroupStatus: (fingerprint: string, status: ErrorGroupStatus) =>
    j<ErrorGroup>(`/v1/admin/error-groups/${fingerprint}`, "PATCH", { status }),
  // ── arkadaşlık ve direkt mesaj (D8, EKRANLAR §3)
  relationships: () => j<Relationship[]>("/v1/users/@me/relationships", "GET"),
  sendFriendRequest: (username: string) =>
    j<Relationship>("/v1/users/@me/relationships", "POST", { username }),
  addRelationship: (userId: string) =>
    j<Relationship>(`/v1/users/@me/relationships/${userId}`, "PUT"),
  removeRelationship: (userId: string) =>
    j<void>(`/v1/users/@me/relationships/${userId}`, "DELETE"),
  blockUser: (userId: string) =>
    j<Relationship>(`/v1/users/@me/relationships/${userId}/block`, "PUT"),
  directChannels: () => j<DMChannel[]>("/v1/users/@me/channels", "GET"),
  createDirectChannel: (input: string | { recipient_id?: string; recipient_ids?: string[]; name?: string }) =>
    j<DMChannel>(
      "/v1/users/@me/channels",
      "POST",
      typeof input === "string" ? { recipient_id: input } : input,
    ),
  // ── bot uygulamaları & geliştirici portalı
  applications: () => j<Application[]>("/v1/oauth2/applications", "GET"),
  createApplication: (b: CreateApplicationRequest) => j<Application>("/v1/oauth2/applications", "POST", b),
  application: (id: string) => j<Application>(`/v1/oauth2/applications/${enc(id)}`, "GET"),
  updateApplication: (id: string, b: UpdateApplicationRequest) => j<Application>(`/v1/oauth2/applications/${enc(id)}`, "PATCH", b),
  resetApplicationToken: (id: string) => j<Application>(`/v1/oauth2/applications/${enc(id)}/reset-token`, "POST"),
  deleteApplication: (id: string) => j<void>(`/v1/oauth2/applications/${enc(id)}`, "DELETE"),
  addBotToGuild: (guildId: string, botId: string) => j<void>(`/v1/guilds/${enc(guildId)}/members/bot/${enc(botId)}`, "POST"),
};

export const fileUrl = (id: string | null | undefined): string | undefined => (id ? `/v1/files/${id}` : undefined);
